import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { del, get, patch, post, put } from '@agribot/polygon-checks-ui'

/** An organisation's people, for its administrators (/org/users). */
export interface OrgUser {
  id: string
  email: string | null
  full_name: string | null
  kind: 'staff' | 'supplier' | 'farmer'
  is_org_admin: boolean
  signed_in: boolean
  provisioned_by_key: boolean
  invited_at: string | null
  suppliers: { id: string; name: string }[]
  farmers: { id: string; code: string | null; name: string | null }[]
  /** Staff roles for supplier data collection; see StaffRole. */
  roles?: StaffRole[]
  /** A supplier login that may invite colleagues to the same suppliers. */
  is_supplier_admin?: boolean
}

export type StaffRole = 'admin' | 'buyer' | 'regional_lead' | 'rnd' | 'viewer'

export const STAFF_ROLES: { value: StaffRole; label: string; help: string }[] = [
  { value: 'admin', label: 'Campaign admin', help: 'Campaigns, suppliers and who reviews them' },
  { value: 'buyer', label: 'Buyer', help: 'First review of their suppliers’ submissions' },
  { value: 'regional_lead', label: 'Regional lead', help: 'Final review for their region' },
  { value: 'rnd', label: 'R&D', help: 'The taxonomy and suppliers’ proposals' },
  { value: 'viewer', label: 'Viewer', help: 'Dashboards and exports' },
]

export const useOrgUsers = () =>
  useQuery({
    queryKey: ['org-users'],
    queryFn: () => get<{ users: OrgUser[] }>('/org/users').then((r) => r.users),
  })

export function useOrgUserMutations() {
  const qc = useQueryClient()
  const after = () => qc.invalidateQueries({ queryKey: ['org-users'] })
  return {
    invite: useMutation({
      mutationFn: (body: { email: string; full_name?: string; kind: OrgUser['kind']; supplier_ids?: string[]; farmer_id?: string | null; locale?: string }) =>
        post<{ id: string; sign_in_url: string }>('/org/users/invitations', body),
      onSuccess: after,
    }),
    change: useMutation({
      mutationFn: ({ id, ...body }: { id: string; kind: OrgUser['kind']; supplier_ids?: string[]; farmer_id?: string | null }) =>
        patch<{ id: string }>(`/org/users/${id}`, body),
      onSuccess: after,
    }),
    setRoles: useMutation({
      mutationFn: ({ id, roles }: { id: string; roles: StaffRole[] }) => put<{ roles: StaffRole[] }>(`/org/users/${id}/roles`, { roles }),
      onSuccess: after,
    }),
    setSupplierAdmin: useMutation({
      mutationFn: ({ id, on }: { id: string; on: boolean }) => put(`/org/users/${id}/supplier-admin`, { is_supplier_admin: on }),
      onSuccess: after,
    }),
    sendLink: useMutation({
      mutationFn: (id: string) => post<{ expires_at: string }>(`/org/users/${id}/sign-in-links`),
    }),
    remove: useMutation({
      mutationFn: (id: string) => del<null>(`/org/users/${id}`),
      onSuccess: after,
    }),
  }
}
